<template>
  <div class="card" :class="{ 'card--expanded': expanded }" :style="expandedStyle">
    <div class="card-hdr" v-if="!hideHeader || loading || error">
      <span class="card-ttl">{{ title }}</span>
      <span v-if="subtitle" class="card-sub">{{ subtitle }}</span>
      <slot name="header-extra" />
      <DxButton
        v-if="expandable"
        class="card-expand-btn"
        :icon="expanded ? 'collapse' : 'expand'"
        :hint="expanded ? 'Collapse' : 'Full screen'"
        styling-mode="text"
        @click="$emit('toggle-expand')"
      />
    </div>

    <div class="card-body">
      <div v-if="loading" class="card-loading">
        <DxLoadIndicator :height="24" :width="24" />
      </div>

      <div v-else-if="error" class="card-error-state">
        <span class="card-error-msg">{{ error }}</span>
        <DxButton
          text="Retry"
          icon="refresh"
          type="normal"
          styling-mode="outlined"
          @click="retry"
        />
      </div>

      <slot v-else :data="data" :expanded="expanded" :body-height="bodyHeight" />
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { DxButton } from "devextreme-vue/button";
import { DxLoadIndicator } from "devextreme-vue/load-indicator";

const CARD_CHROME_HEIGHT = 90; // card header + body padding, not covered by the grid itself
const CARD_CHROME_HEIGHT_NO_HEADER = 35; // card border (2px) + body padding (20px) only, header row is hidden
const EXPANDED_HEIGHT_BUFFER = 24; // slack so the expanded card never touches the viewport edge and triggers an outer page scrollbar

export default {
  name: "GcDashboardCard",
  components: { DxButton, DxLoadIndicator },
  props: {
    title: {
      type: String,
      required: true,
    },
    subtitle: {
      type: String,
      default: "",
    },
    fetch: {
      type: Function,
      required: true,
    },
    expandable: {
      type: Boolean,
      default: false,
    },
    expanded: {
      type: Boolean,
      default: false,
    },
    hideHeader: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      error: null,
      data: null,
    };
  },
  computed: {
    ...mapGetters({ viewportGridHeight: "ui/getHeightForDataGrid" }),
    chromeHeight() {
      return this.hideHeader && !this.loading && !this.error
        ? CARD_CHROME_HEIGHT_NO_HEADER
        : CARD_CHROME_HEIGHT;
    },
    bodyHeight() {
      return this.viewportGridHeight
        ? this.viewportGridHeight - this.chromeHeight - EXPANDED_HEIGHT_BUFFER
        : null;
    },
    expandedStyle() {
      return this.expanded && this.bodyHeight
        ? { height: `${this.bodyHeight + this.chromeHeight}px` }
        : {};
    },
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = null;

      try {
        this.data = await this.fetch();
      } catch (e) {
        this.error = "Failed to load. Please try again.";
      } finally {
        this.loading = false;
      }
    },
    retry() {
      return this.load();
    },
    refresh() {
      return this.load();
    },
  },
};
</script>

<style lang="scss" scoped>
.card {
  background: #fff;
  border: 1px solid var(--base-border-color, #e0e0e0);
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.card--expanded {
  grid-column: 1 / -1;
}

.card-hdr {
  padding: 8px 12px;
  border-bottom: 1px solid var(--base-border-color, #edf0f3);
  display: grid;
  grid-template-columns: 1fr auto 1fr auto;
  align-items: center;
  flex-shrink: 0;
  gap: 8px;
}

.card-expand-btn.dx-button {
  justify-self: end;
}

.card-ttl {
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.5px;
  justify-self: start;
}

.card-sub {
  grid-column: 2;
  font-size: 10px;
  color: #999;
  justify-self: center;
  white-space: nowrap;
}

.card-body {
  padding: 10px 12px;
  flex: 1;
  min-width: 0;
  min-height: 0;
}

.card-placeholder {
  color: #999;
  font-size: 13px;
}

.card-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 0;
}

.card-error-state {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  background: #fff8f8;
  border: 1px solid #f5c6cb;
  border-radius: 6px;
}

.card-error-msg {
  font-size: 13px;
  color: #842029;
}
</style>
